import React, { useEffect, useState, useCallback } from 'react';
import { Bell, BellOff, BellRing, Loader2 } from 'lucide-react';
import { subscribeToPush, unsubscribeFromPush, getCurrentPushStatus, isPushSupported } from '../lib/push';
import { useToast } from '../hooks/use-toast';

/**
 * PushBell — single tile/button that asks for notification permission, subscribes,
 * and lets the user mute/unmute later.
 *
 * Props:
 *   role: 'customer' | 'driver'
 *   authHeaders: object — bearer token headers for the API
 *   variant: 'light' (default, on white UI) | 'dark' (on the dark driver shell)
 *   autoPrompt: bool — if true, prompts for permission once per session on mount
 */
const PushBell = ({ role, authHeaders, variant = 'light', autoPrompt = false }) => {
  const { toast } = useToast();
  const [status, setStatus] = useState('loading'); // loading | unsupported | denied | default | granted-no-sub | subscribed
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    if (!isPushSupported()) {
      setStatus('unsupported');
      return;
    }
    const s = await getCurrentPushStatus();
    setStatus(s);
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Auto-prompt the user to subscribe. For DRIVERS this is operational —
  // every dashboard mount that lands without a subscription must re-prompt
  // (we only skip if the OS-level permission is `denied`, since asking again
  // there is impossible). For customers we keep the once-per-session gate so
  // we don't nag them on every page navigation.
  useEffect(() => {
    if (!autoPrompt) return;
    if (status !== 'default' && status !== 'granted-no-sub') return;
    if (role !== 'driver') {
      const key = `mr_push_prompted_${role}`;
      if (sessionStorage.getItem(key)) return;
      sessionStorage.setItem(key, '1');
    }
    (async () => {
      setBusy(true);
      try {
        const res = await subscribeToPush(role, authHeaders);
        if (res.ok) {
          toast({ title: 'Notifications enabled', description: 'You\'ll get updates about your orders.' });
        }
      } catch {
        // silently ignore
      } finally {
        setBusy(false);
        await refresh();
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, autoPrompt, role]);

  const enable = async () => {
    setBusy(true);
    try {
      const res = await subscribeToPush(role, authHeaders);
      if (res.ok) {
        toast({ title: 'Notifications enabled' });
      } else if (res.reason === 'denied') {
        toast({
          title: 'Notifications blocked',
          description: 'Enable them from your browser site settings, then refresh.',
        });
      } else {
        toast({ title: 'Could not enable notifications' });
      }
    } catch (err) {
      toast({ title: 'Could not enable notifications', description: err?.message || '' });
    } finally {
      setBusy(false);
      await refresh();
    }
  };

  const disable = async () => {
    setBusy(true);
    try {
      await unsubscribeFromPush(role, authHeaders);
      toast({ title: 'Notifications muted' });
    } finally {
      setBusy(false);
      await refresh();
    }
  };

  if (status === 'loading') return null;
  if (status === 'unsupported') {
    return (
      <span
        className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full ${
          variant === 'dark' ? 'bg-white/5 text-slate-400' : 'bg-neutral-100 text-neutral-500'
        }`}
        data-testid="push-unsupported"
      >
        <BellOff className="w-3.5 h-3.5" /> Notifications unsupported
      </span>
    );
  }

  const tone =
    variant === 'dark'
      ? {
          off: 'bg-white/5 hover:bg-white/10 text-slate-200',
          on: 'bg-emerald-500/15 text-emerald-300 ring-1 ring-emerald-400/30 hover:bg-emerald-500/25',
          deny: 'bg-red-500/10 text-red-300',
        }
      : {
          off: 'bg-white border border-neutral-200 text-neutral-700 hover:bg-neutral-50',
          on: 'bg-emerald-50 border border-emerald-200 text-emerald-700 hover:bg-emerald-100',
          deny: 'bg-red-50 border border-red-200 text-red-700',
        };

  if (status === 'denied') {
    return (
      <span
        className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full ${tone.deny}`}
        data-testid="push-denied"
        title="Enable notifications from your browser settings"
      >
        <BellOff className="w-3.5 h-3.5" /> Notifications blocked
      </span>
    );
  }

  if (status === 'subscribed') {
    return (
      <button
        onClick={disable}
        disabled={busy}
        className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full transition ${tone.on}`}
        data-testid="push-mute-btn"
      >
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <BellRing className="w-3.5 h-3.5" />}
        Notifications on
      </button>
    );
  }

  // default | granted-no-sub
  return (
    <button
      onClick={enable}
      disabled={busy}
      className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full transition ${tone.off}`}
      data-testid="push-enable-btn"
    >
      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Bell className="w-3.5 h-3.5" />}
      Enable notifications
    </button>
  );
};

export default PushBell;
